/**
 * Admin polls page — `/admin/polls`.
 *
 * Two surfaces in one route:
 *   1. List view (no `?id=` query) — recent polls grouped by status,
 *      with quick actions (activate / close / delete) per row and a
 *      "+ New poll" button that swaps to the create form.
 *   2. Edit view (`?id=<poll_id>`) — full editor for a single poll:
 *      question, description, settings, options (only editable while
 *      draft), and a live tally chart for active/closed polls.
 *
 * Lifecycle invariants enforced by the backend that drive the UI:
 *   - Options can only be edited while the poll is `draft`.
 *   - `allow_multiple` can only flip while `draft`.
 *   - `activate` auto-closes any other active poll (admin warning).
 *   - `delete` only works on `draft`.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

const STATUS_TONE = {
  draft: { bg: 'rgba(127,180,255,0.18)', border: 'rgba(127,180,255,0.55)', fg: '#7FB4FF' },
  active: { bg: 'rgba(95,224,190,0.18)', border: 'rgba(95,224,190,0.55)', fg: '#5FE0BE' },
  closed: { bg: 'rgba(160,150,200,0.18)', border: 'rgba(160,150,200,0.55)', fg: '#A096C8' },
};

function _idFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('id');
}

export async function renderAdminPolls(root) {
  const editId = _idFromQuery();
  if (editId === 'new') {
    renderCreateForm(root);
    return;
  }
  if (editId) {
    await renderEdit(root, editId);
    return;
  }
  await renderList(root);
}

// ---- list ------------------------------------------------------------------

async function renderList(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Polls</div>
      <div class="appbar-spacer"></div>
      <a href="/admin/polls?id=new" data-link class="btn sm gold" style="margin:0;width:auto;padding:6px 14px;">+ New</a>
    </div>
    <div class="pad" id="polls-body">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  let rows;
  try {
    rows = await api('/admin/polls');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    document.getElementById('polls-body').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Failed to load.')}</div>`;
    return;
  }
  const body = document.getElementById('polls-body');
  if (!rows.length) {
    body.innerHTML = `
      <div class="card center muted" style="padding:24px;">
        <div style="font-family:var(--font-display);font-size:14px;color:#fff;margin-bottom:6px;">No polls yet</div>
        <div style="font-size:12px;">Create your first poll to start gathering votes.</div>
      </div>
    `;
    return;
  }

  // Show active first (most operationally relevant), then draft, then closed.
  const order = { active: 0, draft: 1, closed: 2 };
  rows.sort((a, b) => (order[a.status] - order[b.status]) || (b.created_at.localeCompare(a.created_at)));
  body.innerHTML = rows.map(pollRow).join('');
  body.querySelectorAll('[data-action]').forEach((el) =>
    el.addEventListener('click', (e) => onListAction(e, el)),
  );
}

function pollRow(p) {
  const tone = STATUS_TONE[p.status];
  const closesLine = p.closes_at
    ? `Closes ${formatDate(p.closes_at)}`
    : (p.status === 'closed' ? `Closed ${formatDate(p.closed_at || p.updated_at)}` : 'No deadline');
  const subBits = [
    `${p.options.length} option${p.options.length === 1 ? '' : 's'}`,
    p.allow_multiple ? 'multi-select' : 'single-select',
    `${p.total_ballots} ballot${p.total_ballots === 1 ? '' : 's'}`,
    closesLine,
  ];
  // Status-specific actions inline so admin doesn't have to drill into
  // the edit view for the common one-tap operations.
  const actions = [];
  actions.push(`<a href="/admin/polls?id=${escape(p.id)}" data-link class="btn ghost sm" style="margin:0;width:auto;padding:6px 12px;">Open</a>`);
  if (p.status === 'draft') {
    actions.push(`<button type="button" data-action="activate" data-id="${escape(p.id)}" class="btn sm teal" style="margin:0;width:auto;padding:6px 12px;">Activate</button>`);
    actions.push(`<button type="button" data-action="delete" data-id="${escape(p.id)}" class="btn sm" style="margin:0;width:auto;padding:6px 10px;background:rgba(233,75,162,0.18);border:1px solid rgba(233,75,162,0.55);color:#fff;">Delete</button>`);
  } else if (p.status === 'active') {
    actions.push(`<button type="button" data-action="close" data-id="${escape(p.id)}" class="btn sm" style="margin:0;width:auto;padding:6px 12px;background:rgba(160,150,200,0.22);border:1px solid rgba(160,150,200,0.55);color:#fff;">Close</button>`);
  }
  return `
    <div class="card" style="padding:14px;">
      <div class="row" style="gap:10px;align-items:flex-start;">
        <span style="display:inline-block;padding:3px 9px;border-radius:6px;background:${tone.bg};border:1px solid ${tone.border};color:${tone.fg};font-family:var(--font-display);font-size:10px;font-weight:700;letter-spacing:0.10em;text-transform:uppercase;">
          ${escape(p.status)}
        </span>
        <div class="grow" style="min-width:0;">
          <b style="font-size:14px;color:#fff;font-weight:700;line-height:1.3;">${escape(p.question)}</b>
          <div class="muted" style="font-size:11.5px;margin-top:4px;">${escape(subBits.join(' · '))}</div>
        </div>
      </div>
      <div class="row" style="gap:8px;margin-top:10px;flex-wrap:wrap;">
        ${actions.join('')}
      </div>
    </div>
  `;
}

async function onListAction(_e, el) {
  const action = el.dataset.action;
  const id = el.dataset.id;
  if (!action || !id) return;
  if (action === 'delete' && !confirm('Delete this draft poll? This is permanent.')) return;
  if (action === 'activate'
    && !confirm('Activate this poll? Any other active poll will be closed automatically.')) {
    return;
  }
  el.disabled = true;
  try {
    if (action === 'activate') {
      await api(`/admin/polls/${encodeURIComponent(id)}/activate`, { method: 'POST' });
      toast.success('Poll activated.');
    } else if (action === 'close') {
      await api(`/admin/polls/${encodeURIComponent(id)}/close`, { method: 'POST' });
      toast.success('Poll closed.');
    } else if (action === 'delete') {
      await api(`/admin/polls/${encodeURIComponent(id)}`, { method: 'DELETE' });
      toast.success('Poll deleted.');
    }
    renderAdminPolls(document.getElementById('route-shell'));
  } catch (err) {
    toast.error(err?.message || 'Action failed.');
    el.disabled = false;
  }
}

// ---- create form -----------------------------------------------------------

function renderCreateForm(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/admin/polls" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">New poll</div>
    </div>
    <div class="pad">
      <div class="card" style="padding:14px;">
        <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;">Question</label>
        <input id="pf-question" class="field-input" placeholder="What's next week's trivia topic?" maxlength="280" />
        <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:10px;display:block;">Description (optional)</label>
        <textarea id="pf-description" class="field-input" rows="2" maxlength="1000" placeholder="One line context for voters"></textarea>

        <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:14px;display:block;">Options</label>
        <div id="pf-options"></div>
        <button type="button" id="pf-add-option" class="btn ghost sm" style="margin-top:6px;width:auto;padding:6px 14px;">+ Add option</button>

        <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:14px;display:block;">Vote rules</label>
        <label style="display:flex;align-items:center;gap:8px;margin-top:6px;font-size:13px;color:#fff;">
          <input type="checkbox" id="pf-multi" />
          Allow voters to pick multiple options
        </label>

        <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:14px;display:block;">Deadline (optional)</label>
        <input id="pf-deadline" type="datetime-local" class="field-input" />
        <div class="muted" style="font-size:11px;margin-top:4px;">Leave blank for "open until I close it manually". Times are local; the server stores UTC.</div>

        <button type="button" id="pf-create" class="btn gold" style="margin-top:18px;">Create poll (draft)</button>
        <div class="muted" style="font-size:11.5px;margin-top:8px;text-align:center;">
          You'll review it in the editor before activating.
        </div>
      </div>
    </div>
  `;
  // Seed with two empty option fields — the minimum a poll needs.
  const optionsHost = document.getElementById('pf-options');
  optionsHost.innerHTML = [0, 1].map(optionInputRow).join('');
  document.getElementById('pf-add-option').addEventListener('click', () => {
    const next = optionsHost.querySelectorAll('.pf-opt-row').length;
    if (next >= 12) { toast.warn('Max 12 options per poll.'); return; }
    optionsHost.insertAdjacentHTML('beforeend', optionInputRow(next));
    wireRemoveButtons();
  });
  wireRemoveButtons();

  document.getElementById('pf-create').addEventListener('click', onCreateSubmit);
}

function optionInputRow(i) {
  return `
    <div class="pf-opt-row row" style="gap:6px;margin-top:6px;align-items:center;">
      <span class="muted" style="font-size:11px;min-width:18px;">${i + 1}.</span>
      <input class="field-input pf-opt" placeholder="Option ${i + 1}" maxlength="140" style="flex:1;margin:0;" />
      <button type="button" class="btn ghost sm pf-opt-remove" style="margin:0;width:auto;padding:6px 10px;font-size:13px;line-height:1;" title="Remove">×</button>
    </div>
  `;
}

function wireRemoveButtons() {
  document.querySelectorAll('.pf-opt-remove').forEach((btn) => {
    btn.onclick = () => {
      const rows = document.querySelectorAll('.pf-opt-row');
      if (rows.length <= 2) { toast.warn('A poll needs at least 2 options.'); return; }
      btn.closest('.pf-opt-row').remove();
    };
  });
}

async function onCreateSubmit() {
  const question = document.getElementById('pf-question').value.trim();
  const description = document.getElementById('pf-description').value.trim();
  const optionInputs = Array.from(document.querySelectorAll('.pf-opt'));
  const options = optionInputs.map((i) => i.value.trim()).filter(Boolean);
  const allow_multiple = document.getElementById('pf-multi').checked;
  const deadlineRaw = document.getElementById('pf-deadline').value;
  const closes_at = deadlineRaw ? new Date(deadlineRaw).toISOString() : null;

  if (!question) { toast.error('Question is required.'); return; }
  if (options.length < 2) { toast.error('Add at least two options.'); return; }

  const btn = document.getElementById('pf-create');
  btn.disabled = true;
  btn.textContent = 'Creating…';
  try {
    const body = { question, description: description || null, options, allow_multiple };
    if (closes_at) body.closes_at = closes_at;
    const poll = await api('/admin/polls', { method: 'POST', body });
    toast.success('Draft poll created.');
    navigate(`/admin/polls?id=${encodeURIComponent(poll.id)}`);
  } catch (err) {
    btn.disabled = false;
    btn.textContent = 'Create poll (draft)';
    toast.error(err?.message || 'Could not create poll.');
  }
}

// ---- edit / results --------------------------------------------------------

async function renderEdit(root, id) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/admin/polls" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Poll</div>
    </div>
    <div class="pad" id="poll-edit-body">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;
  await refreshEdit(id);
}

async function refreshEdit(id) {
  const host = document.getElementById('poll-edit-body');
  let poll;
  try {
    poll = await api(`/admin/polls/${encodeURIComponent(id)}`);
  } catch (err) {
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Failed to load poll.')}</div>`;
    return;
  }
  const tone = STATUS_TONE[poll.status];
  const isDraft = poll.status === 'draft';
  const isActive = poll.status === 'active';

  host.innerHTML = `
    <div class="card" style="padding:14px;">
      <div class="row" style="gap:10px;align-items:flex-start;">
        <span style="display:inline-block;padding:3px 9px;border-radius:6px;background:${tone.bg};border:1px solid ${tone.border};color:${tone.fg};font-family:var(--font-display);font-size:10px;font-weight:700;letter-spacing:0.10em;text-transform:uppercase;">
          ${escape(poll.status)}
        </span>
        <div class="grow"></div>
      </div>

      <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:12px;display:block;">Question</label>
      <input id="pe-question" class="field-input" value="${escape(poll.question)}" maxlength="280" />
      <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:10px;display:block;">Description</label>
      <textarea id="pe-description" class="field-input" rows="2" maxlength="1000">${escape(poll.description || '')}</textarea>

      <label style="display:flex;align-items:center;gap:8px;margin-top:12px;font-size:13px;color:${isDraft ? '#fff' : 'var(--mute)'};">
        <input type="checkbox" id="pe-multi" ${poll.allow_multiple ? 'checked' : ''} ${isDraft ? '' : 'disabled'} />
        Allow multiple options per ballot
        ${isDraft ? '' : '<span style="font-size:11px;color:var(--mute);margin-left:6px;">(locked after activation)</span>'}
      </label>

      <label style="font-size:11px;color:var(--mute);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;margin-top:14px;display:block;">Deadline</label>
      <div class="row" style="gap:8px;align-items:center;">
        <input id="pe-deadline" type="datetime-local" class="field-input" style="flex:1;margin:0;"
               value="${poll.closes_at ? toLocalInputValue(poll.closes_at) : ''}" />
        <button type="button" id="pe-clear-deadline" class="btn ghost sm" style="margin:0;width:auto;padding:6px 10px;">Clear</button>
      </div>

      <button type="button" id="pe-save" class="btn teal" style="margin-top:14px;">Save changes</button>
    </div>

    <h3 class="sec">Options</h3>
    <div id="pe-options"></div>
    ${isDraft
      ? `<div class="row" style="gap:8px;margin-top:8px;">
           <input id="pe-new-option" class="field-input" placeholder="Add another option" maxlength="140" style="flex:1;margin:0;" />
           <button type="button" id="pe-add-option" class="btn ghost sm" style="margin:0;width:auto;padding:6px 14px;">Add</button>
         </div>`
      : '<div class="muted" style="font-size:11.5px;margin-top:6px;">Options are locked once a poll is activated.</div>'
    }

    <h3 class="sec">Status</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:11.5px;line-height:1.45;">
        ${isDraft ? 'Draft polls are not visible to voters. Activate when ready — any other active poll will be auto-closed.' : ''}
        ${isActive ? 'Active polls accept votes until the deadline (or manual close).' : ''}
        ${poll.status === 'closed' ? 'Closed polls are read-only. The tally below is final.' : ''}
      </div>
      <div class="row" style="gap:8px;margin-top:12px;flex-wrap:wrap;">
        ${isDraft
          ? `<button type="button" id="pe-activate" class="btn teal" style="margin:0;width:auto;padding:8px 18px;">Activate</button>`
          : ''}
        ${isActive
          ? `<button type="button" id="pe-close" class="btn" style="margin:0;width:auto;padding:8px 18px;background:rgba(160,150,200,0.22);border:1px solid rgba(160,150,200,0.55);color:#fff;">Close now</button>`
          : ''}
        ${isDraft
          ? `<button type="button" id="pe-delete" class="btn" style="margin:0;width:auto;padding:8px 18px;background:rgba(233,75,162,0.18);border:1px solid rgba(233,75,162,0.55);color:#fff;">Delete</button>`
          : ''}
      </div>
    </div>

    <h3 class="sec">Results — ${poll.total_ballots} ballot${poll.total_ballots === 1 ? '' : 's'}</h3>
    ${renderResultsBars(poll)}
  `;

  paintOptionsList(poll);

  document.getElementById('pe-save').addEventListener('click', () => onSaveMetadata(id));
  document.getElementById('pe-clear-deadline').addEventListener('click', () => {
    document.getElementById('pe-deadline').value = '';
  });
  document.getElementById('pe-activate')?.addEventListener('click', () => onActivate(id));
  document.getElementById('pe-close')?.addEventListener('click', () => onClose(id));
  document.getElementById('pe-delete')?.addEventListener('click', () => onDelete(id));
  if (isDraft) {
    document.getElementById('pe-add-option').addEventListener('click', () => onAddOption(id));
  }
}

function paintOptionsList(poll) {
  const host = document.getElementById('pe-options');
  if (!host) return;
  const editable = poll.status === 'draft';
  host.innerHTML = (poll.options || []).map((o) => `
    <div class="card" style="padding:10px 12px;margin-bottom:6px;">
      <div class="row" style="gap:8px;">
        <span class="muted" style="font-size:11px;min-width:22px;">${o.position + 1}.</span>
        <div class="grow" style="min-width:0;">
          <b style="color:#fff;font-size:13.5px;">${escape(o.label)}</b>
          <div class="muted" style="font-size:11px;margin-top:2px;">${o.vote_count} vote${o.vote_count === 1 ? '' : 's'}</div>
        </div>
        ${editable
          ? `<button type="button" data-remove="${escape(o.id)}" class="btn ghost sm" style="margin:0;width:auto;padding:6px 10px;">Remove</button>`
          : ''}
      </div>
    </div>
  `).join('');
  host.querySelectorAll('[data-remove]').forEach((btn) =>
    btn.addEventListener('click', () => onRemoveOption(poll.id, btn.dataset.remove)),
  );
}

function renderResultsBars(poll) {
  if (!poll.options.length) return '';
  const max = Math.max(1, ...poll.options.map((o) => o.vote_count || 0));
  return `
    <div class="card" style="padding:14px;">
      ${poll.options.map((o) => {
        const pct = Math.round(((o.vote_count || 0) / max) * 100);
        return `
          <div style="margin-bottom:10px;">
            <div class="row" style="font-size:12.5px;color:#fff;">
              <div class="grow" style="min-width:0;">${escape(o.label)}</div>
              <div style="font-family:var(--font-display);color:var(--gold-t);font-weight:700;">${o.vote_count}</div>
            </div>
            <div style="height:6px;background:#1a1432;border-radius:4px;margin-top:4px;overflow:hidden;">
              <div style="height:100%;width:${pct}%;background:linear-gradient(90deg,#5B2C91,#E94BA2);"></div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

async function onSaveMetadata(id) {
  const body = {
    question: document.getElementById('pe-question').value.trim(),
    description: document.getElementById('pe-description').value.trim(),
    allow_multiple: document.getElementById('pe-multi').checked,
  };
  const deadlineRaw = document.getElementById('pe-deadline').value;
  if (deadlineRaw) {
    body.closes_at = new Date(deadlineRaw).toISOString();
  } else {
    body.clear_closes_at = true;
  }
  const btn = document.getElementById('pe-save');
  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = 'Saving…';
  try {
    await api(`/admin/polls/${encodeURIComponent(id)}`, { method: 'PATCH', body });
    toast.success('Saved.');
    await refreshEdit(id);
  } catch (err) {
    toast.error(err?.message || 'Could not save.');
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}

async function onActivate(id) {
  if (!confirm('Activate this poll? Any other active poll will be auto-closed.')) return;
  try {
    await api(`/admin/polls/${encodeURIComponent(id)}/activate`, { method: 'POST' });
    toast.success('Poll activated.');
    await refreshEdit(id);
  } catch (err) {
    toast.error(err?.message || 'Could not activate.');
  }
}

async function onClose(id) {
  if (!confirm('Close this poll? Voters can no longer submit ballots.')) return;
  try {
    await api(`/admin/polls/${encodeURIComponent(id)}/close`, { method: 'POST' });
    toast.success('Poll closed.');
    await refreshEdit(id);
  } catch (err) {
    toast.error(err?.message || 'Could not close.');
  }
}

async function onDelete(id) {
  if (!confirm('Delete this draft poll? This is permanent.')) return;
  try {
    await api(`/admin/polls/${encodeURIComponent(id)}`, { method: 'DELETE' });
    toast.success('Poll deleted.');
    navigate('/admin/polls');
  } catch (err) {
    toast.error(err?.message || 'Could not delete.');
  }
}

async function onAddOption(id) {
  const input = document.getElementById('pe-new-option');
  const label = input.value.trim();
  if (!label) return;
  try {
    await api(`/admin/polls/${encodeURIComponent(id)}/options`, {
      method: 'POST',
      body: { label },
    });
    input.value = '';
    toast.success('Option added.');
    await refreshEdit(id);
  } catch (err) {
    toast.error(err?.message || 'Could not add option.');
  }
}

async function onRemoveOption(pollId, optionId) {
  if (!confirm('Remove this option?')) return;
  try {
    await api(
      `/admin/polls/${encodeURIComponent(pollId)}/options/${encodeURIComponent(optionId)}`,
      { method: 'DELETE' },
    );
    toast.success('Option removed.');
    await refreshEdit(pollId);
  } catch (err) {
    toast.error(err?.message || 'Could not remove option.');
  }
}

// ---- utilities -------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function toLocalInputValue(iso) {
  // datetime-local needs "YYYY-MM-DDTHH:mm" in local time (no tz suffix).
  // new Date(iso) reads UTC, .toLocale* converts to local; we slice the
  // ISO-ish form manually because there's no built-in `toLocalISOString`.
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
